import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, Details, IconButton, LogStream, StatusChip, StepList,
  StepProgress, TopAppBar,
} from '@meshble/molo';
import { api, must } from '../lib/api';
import { useJobLog } from '../lib/sse';
import { useIstanze, useServers } from '../lib/dati';
import { avanzamento, esitoOk, nomePasso, passi, titolo, type KindJob } from '../lib/attivita';
import { localeIntl, t } from '../lib/lingua';
import { ETICHETTA, TONO } from './jobs';
import { Back, Done, Warn } from '../icons';
import { CaricoSezione, Logo, LogoAvanzamento } from '../brand';

/**
 * Un'attività mentre succede, e dopo.
 *
 * Chi apre questa schermata aspetta che qualcosa finisca: un server da
 * installare, un'istanza da creare. Quello che vuole sapere è a che punto è,
 * cosa sta facendo adesso e se è andata bene. I passi glielo dicono con
 * parole sue; il log — le righe vere, quelle dell'hub e degli script — sta
 * sotto «Dettagli tecnici», aperto da solo quando qualcosa è fallito, perché
 * allora è lì che c'è la risposta.
 *
 * Le righe arrivano in SSE, non dalla query: la query serve solo per
 * intestazione e stato iniziale. Alla riconnessione EventSource rimanda da
 * solo `Last-Event-ID` e l'hub riprende dallo `seq` giusto, quindi non c'è
 * niente da riconciliare qui.
 */
export function JobPage() {
  const { slug, id } = useParams({ from: '/shell/orgs/$slug/jobs/$id' });
  const navigate = useNavigate();
  const { lines, done, error } = useJobLog(slug, id);

  const job = useQuery({
    queryKey: ['job', slug, id],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/jobs/{id}', { params: { path: { slug, id } } })),
    // Lo stato finale arriva dall'evento `end`: finché non arriva, l'unica
    // fonte è la riga del job, e un job può fallire senza scrivere altro log.
    refetchInterval: done ? false : 10000,
  });

  const servers = useServers(slug);
  const istanze = useIstanze(slug);
  const j = job.data;
  const kind = (j?.kind ?? '') as KindJob;
  const server = (servers.data?.servers ?? []).find((s) => s.id === j?.serverId);
  const istanza = (istanze.data?.instances ?? []).find((i) => i.id === j?.instanceId);
  // Il nome nel titolo: l'istanza se il job ne ha una, altrimenti il server.
  const bersaglio = j?.instanceId ? istanza?.name : server?.name;

  // Lo stato vero è quello dell'evento `end` se è arrivato; prima, quello
  // della riga del job.
  const stato = done?.status ?? j?.status;
  const inCorso = stato === 'running' || stato === 'queued';
  const fallito = stato === 'failed' || stato === 'cancelled';
  const riuscito = stato === 'succeeded';
  const errore = done?.error || j?.error;

  const dove = avanzamento(lines);
  const elenco = passi(kind, lines, stato);
  const conPassi = elenco.length > 0;

  // L'istanza del job può non esserci più: una creazione fallita e
  // compensata sparisce dall'hub, e la sua scheda darebbe «non trovata».
  const istanzaViva = !!j?.instanceId && kind !== 'delete_instance' && !(istanze.isSuccess && !istanza);

  // Dove si torna: alla cosa su cui si stava lavorando, non a un elenco.
  const indietro = () => {
    if (istanzaViva) navigate({ to: '/orgs/$slug/instances/$id', params: { slug, id: j!.instanceId! } });
    else if (istanza || kind.endsWith('_instance')) navigate({ to: '/orgs/$slug/instances', params: { slug } });
    else if (j?.serverId) navigate({ to: '/orgs/$slug/servers/$id', params: { slug, id: j.serverId } });
    else navigate({ to: '/orgs/$slug/jobs', params: { slug } });
  };

  // Dopo, l'azione che ha senso: vedere il server pronto, l'istanza nuova, il backup.
  const prossimo = () => {
    if (kind === 'provision_server' && j?.serverId) navigate({ to: '/orgs/$slug/servers/$id', params: { slug, id: j.serverId } });
    else if (kind === 'backup_instance') navigate({ to: '/orgs/$slug/backup', params: { slug }, search: { istanza: j?.instanceId } });
    else if (istanzaViva) navigate({ to: '/orgs/$slug/instances/$id', params: { slug, id: j!.instanceId! } });
    else if (kind.endsWith('_instance')) navigate({ to: '/orgs/$slug/instances', params: { slug } });
    else navigate({ to: '/orgs/$slug/jobs', params: { slug } });
  };
  const etichettaProssimo =
    kind === 'provision_server' ? t('Vai al server')
    : kind === 'backup_instance' ? t('Vai ai backup')
    : kind === 'delete_instance' ? t('Vai alle istanze')
    : istanzaViva ? t('Vai all’istanza')
    : kind.endsWith('_instance') ? t('Vai alle istanze')
    : t('Alle attività');

  return (
    <>
      <TopAppBar
        leading={<IconButton label={t('Indietro')} onClick={indietro}><Back /></IconButton>}
        title={j ? titolo(j.kind, bersaglio) : '…'}
        subtitle={j ? t('Avviata {quando}', { quando: new Date(j.startedAt ?? j.createdAt).toLocaleString(localeIntl(), { dateStyle: 'short', timeStyle: 'short' }) }) : undefined}
        actions={stato ? <StatusChip status={stato} label={t(ETICHETTA[stato])} tone={TONO[stato]} /> : undefined}
      />
      <CaricoSezione attiva={job.isPending} />
      <div className="md-content">
        <div className="md-stack">
          {riuscito && (
            <Banner
              tone="success"
              icon={<Done />}
              title={t('Fatto')}
              action={<Button variant="text" onClick={prossimo}>{etichettaProssimo}</Button>}
            >
              {esitoOk(kind) ?? t('L’attività è terminata.')}
            </Banner>
          )}
          {fallito && (
            <Banner
              tone="error"
              icon={<Warn />}
              title={stato === 'cancelled' ? t('Attività annullata') : t('Non è andata a buon fine')}
              action={j?.serverId && kind === 'provision_server' ? (
                <Button variant="outlined" onClick={prossimo}>{t('Vai al server')}</Button>
              ) : istanzaViva ? (
                <Button variant="outlined" onClick={prossimo}>{t('Vai all’istanza')}</Button>
              ) : kind.endsWith('_instance') ? (
                <Button variant="outlined" onClick={prossimo}>{t('Vai alle istanze')}</Button>
              ) : undefined}
            >
              <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-1)' }}>
                <span>
                  {dove.chiave ? t('Si è fermata a «{passo}».', { passo: nomePasso(dove.chiave, kind) }) : t('Si è fermata prima di iniziare.')}
                  {kind === 'provision_server' && ` ${t('Dalla scheda del server puoi riprenderla da lì, senza rifare il resto.')}`}
                  {kind === 'create_instance' && ` ${t('Quello che era stato creato è stato tolto: puoi riprovare.')}`}
                  {kind === 'restore_instance' && ` ${t('La copia parziale è stata tolta: l’originale non è stata toccata.')}`}
                  {kind === 'configure_instance' && ` ${t('La configurazione può essere rimasta a metà: guarda il log e rigenerala dalla scheda dell’istanza.')}`}
                  {kind === 'restore_in_place' && ` ${t('Se si è fermato prima dello scambio, i dati attuali non sono stati toccati. Se dopo, quelli precedenti sono rimasti sul server con il suffisso _old, e il log dice dove. In ogni caso c’è il backup di sicurezza fatto all’inizio.')}`}
                  {kind === 'test_restore' && ` ${t('L’istanza non è stata toccata: la prova ricarica il backup in un database a parte. Ma quel backup non si ricarica, e il log dice perché.')}`}
                  {(kind === 'start_instance' || kind === 'restart_instance') && ` ${t('Odoo non ha risposto in tempo: il log qui sotto dice perché.')}`}
                </span>
                {errore && <span className="md-mono" style={{ opacity: .85 }}>{errore}</span>}
              </div>
            </Banner>
          )}
          {error && (
            <Banner
              tone="error"
              title={t('Ho perso il collegamento')}
              action={<Button variant="text" onClick={() => location.reload()}>{t('Ricarica')}</Button>}
            >
              {t("L'attività continua sul server anche se qui non arriva più niente. Ricaricando riprendi da dove eri.")}
            </Banner>
          )}

          <Card variant="outlined">
            {conPassi ? (
              <>
                {/* Il molo cresce coi passi veri: la quantità di segno disegnata
                    è la percentuale di passi conclusi, e l'acqua arriva alla
                    fine. Fallito, resta dov'è e passa al colore dell'errore. */}
                {dove.total > 0 || !inCorso ? (
                  <div className="md-avanzamento">
                    <LogoAvanzamento
                      step={riuscito ? elenco.length : dove.step}
                      total={dove.total || elenco.length}
                      size={56}
                      failed={fallito}
                    />
                    <StepProgress
                      step={riuscito ? elenco.length : dove.step}
                      total={dove.total || elenco.length}
                      label={
                        riuscito ? t('Tutti i passi conclusi')
                        : fallito ? (dove.chiave ? t('Ferma a «{passo}»', { passo: nomePasso(dove.chiave, kind) }) : t('Ferma prima di iniziare'))
                        : dove.chiave ? nomePasso(dove.chiave, kind) : t('Sto iniziando')
                      }
                    />
                  </div>
                ) : (
                  // In coda: si sta stabilendo il contatto con la macchina.
                  <div className="md-attesa">
                    <Logo size={56} anim="ormeggio" label={t('In coda')} />
                    <p className="md-body-medium">{t('In coda: parte tra pochi secondi.')}</p>
                  </div>
                )}
                <StepList className="md-passi" items={elenco} />
              </>
            ) : (
              <>
                {inCorso && (
                  // Livello: un'attesa con un fine ma senza percentuale.
                  <div className="md-attesa">
                    <Logo size={56} anim="livello" label={t('In corso')} />
                    <p className="md-body-medium">
                      {kind === 'backup_instance'
                        ? t('Sto salvando database e allegati nella destinazione. Un backup di qualche gigabyte richiede alcuni minuti.')
                        : t('In corso. Ci vuole poco.')}
                    </p>
                  </div>
                )}
                {!inCorso && !stato && (
                  <div className="md-attesa"><Logo size={56} anim="marea" label={t('Carico…')} /><p className="md-body-medium">{t('Carico l’attività…')}</p></div>
                )}
                {!inCorso && stato && (
                  <div className="md-attesa">
                    <Logo size={56} tone={fallito ? 'error' : 'primary'} />
                    <p className="md-body-medium" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                      {riuscito ? t('Terminata senza problemi.') : t('Terminata.')}
                    </p>
                  </div>
                )}
              </>
            )}
          </Card>

          <Details summary={t('Dettagli tecnici')} open={fallito || undefined}>
            <dl className="md-dati">
              <div className="md-dato"><dt>{t('Identificativo')}</dt><dd className="md-mono">{id}</dd></div>
              {j && <div className="md-dato"><dt>{t('Tipo')}</dt><dd className="md-mono">{j.kind}</dd></div>}
              {j?.finishedAt && <div className="md-dato"><dt>{t('Conclusa')}</dt><dd>{new Date(j.finishedAt).toLocaleString(localeIntl())}</dd></div>}
            </dl>
            <div className="md-sezione">
              <LogStream
                lines={lines}
                maxHeight="calc(100vh - 22rem)"
                empty={
                  inCorso
                    ? t('Le righe compaiono qui appena arrivano.')
                    : t('Questa attività non ha scritto niente nel log.')
                }
              />
            </div>
          </Details>
        </div>
      </div>
    </>
  );
}
